import { FC, useState, useEffect, useCallback } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useTranslation } from 'react-i18next';
import moment, { Moment } from 'moment-timezone';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';

type MonthPickerProps = {
  value: Moment | null;
  label?: string;
  onChange: (newValue: Moment | null) => void;
  minDate?: Moment;
  maxDate?: Moment;
  readOnly?: boolean;
  isValid?: boolean;
  startDate?: Moment;
  onlyYear?: boolean;
  onlyMonth?: boolean;
} & DatePickerProps<moment.Moment>;

const MonthPicker: FC<MonthPickerProps> = ({
  value,
  label,
  onChange,
  minDate = moment('2024-01-01').utc().startOf('year'),
  maxDate = moment().utc().endOf('month'),
  startDate,
  readOnly = false,
  isValid = true,
  onlyYear = false,
  onlyMonth = false,
  ...others
}) => {
  const { i18n } = useTranslation();
  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(value);

  let views: Array<'year' | 'month'>;
  if (onlyYear) {
    views = ['year'];
  } else if (onlyMonth) {
    views = ['month'];
  } else {
    views = ['year', 'month'];
  }

  useEffect(() => {
    if (startDate && currentDate && startDate > currentDate) {
      const newEndDate = startDate.clone().utc().endOf('month');
      setCurrentDate(newEndDate);
      onChange(newEndDate);
    }
  }, [startDate, currentDate, onChange]);

  const handleMonthChange = useCallback(
    (newValue: Moment | null) => {
      if (newValue) {
        onChange(newValue);
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  if (!isValid && currentDate) {
    setCurrentDate(null);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={i18n.language}>
      <DatePicker
        label={label}
        views={views}
        value={currentDate}
        onChange={setCurrentDate}
        onAccept={handleMonthChange}
        readOnly={readOnly}
        minDate={minDate}
        maxDate={maxDate}
        {...others}
      />
    </LocalizationProvider>
  );
};

export default MonthPicker;
